import { initSentry } from '@/lib/sentry';
// Sentry MUST be initialised before any other import that may throw and
// before `createRoot` so the very first render-phase exception is captured.
initSentry();

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ColorSchemeScript, MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { queryClient } from '@/lib/query-client';
import { AppRoutes } from '@/lib/router';
import { registerChunkReloadHandler } from '@/lib/lazy-with-reload';
import { AuthProvider } from '@/lib/auth-provider';
import { ADMIN_DATES_SETTINGS } from '@/lib/dates-settings';
import { ErrorBoundary } from '@/lib/error-boundary';
import { PlayerProvider } from '@/lib/player/PlayerProvider';
import { ResponsiveProvider } from '@/lib/responsive-context';
import { SessionExpiredModal } from '@/lib/session-expired-modal';
import { SocketProvider } from '@/lib/SocketProvider';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

// BUG-141: recover from a post-deploy / stalled lazy-route chunk load with one
// guarded reload (pairs with lazyWithReload() in router.tsx). Register before
// any route chunk can fail.
registerChunkReloadHandler();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

// Wrapping strategy:
//   * `Sentry.ErrorBoundary` is the outermost boundary so render errors that
//     escape the app-local boundary (e.g. crashes inside provider mounts)
//     are still reported.
//   * The app-local `ErrorBoundary` catches first because it is nearer in the
//     tree; it now also forwards the error to Sentry via
//     `Sentry.captureException` in `componentDidCatch` so a single failure
//     produces exactly one event regardless of which boundary handles it.
createRoot(rootElement).render(
  <StrictMode>
    <ColorSchemeScript defaultColorScheme="auto" />
    <Sentry.ErrorBoundary fallback={<></>}>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <SocketProvider>
                <MantineProvider defaultColorScheme="auto">
                  <DatesProvider settings={ADMIN_DATES_SETTINGS}>
                    <ResponsiveProvider>
                      <PlayerProvider>
                        <SessionExpiredModal />
                        <AppRoutes />
                      </PlayerProvider>
                    </ResponsiveProvider>
                  </DatesProvider>
                </MantineProvider>
              </SocketProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
